import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import {
  createLeadQualificationQuestion,
  deleteLeadQualificationQuestion,
  getLeadQualificationQuestions,
  seedLeadQualificationQuestionDefaults,
  updateLeadQualificationQuestion
} from '../../services/api/implementation/impl';
import { Pencil, Plus, Trash2, X } from 'lucide-react';

const QUESTION_TYPES = [
  { value: 'select', label: 'Select' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & time' }
];

const TRANSITIONS = [
  { value: 'all', label: 'All transitions' },
  { value: 'cold_to_warm', label: 'Cold → Warm' },
  { value: 'warm_to_hot', label: 'Warm → Hot' },
  { value: 'to_nurture', label: 'To Nurture' }
];

const emptyForm = () => ({
  label: '',
  type: 'select',
  required: true,
  transition: 'all',
  options: [{ value: '', label: '' }]
});

function OptionEditor({ options, onChange }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-600">
        Options (selected answers become filterable tags on the lead)
      </div>
      {options.map((opt, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input
            className="flex-1 border rounded px-2 py-1.5 text-sm"
            placeholder="Option label"
            value={opt.label}
            onChange={(e) => {
              const next = [...options];
              next[idx] = { ...next[idx], label: e.target.value };
              onChange(next);
            }}
          />
          <button
            type="button"
            className="p-1.5 text-gray-500 hover:text-red-600"
            onClick={() => onChange(options.filter((_, i) => i !== idx))}
            disabled={options.length <= 1}
            title="Remove option">
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-[#2A565E] font-medium inline-flex items-center gap-1"
        onClick={() => onChange([...options, { value: '', label: '' }])}>
        <Plus size={12} /> Add option
      </button>
    </div>
  );
}

function QuestionFormFields({ form, setForm, needsOptions }) {
  return (
    <div className="space-y-3">
      <input
        className="w-full border rounded px-2 py-1.5 text-sm"
        placeholder="Question label"
        value={form.label}
        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select
          className="border rounded px-2 py-1.5 text-sm"
          value={form.type}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              type: e.target.value,
              options:
                e.target.value === 'select' || e.target.value === 'multiselect'
                  ? f.options?.length
                    ? f.options
                    : [{ value: '', label: '' }]
                  : f.options
            }))
          }>
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-1.5 text-sm"
          value={form.transition}
          onChange={(e) => setForm((f) => ({ ...f, transition: e.target.value }))}>
          {TRANSITIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 px-1">
          <input
            type="checkbox"
            checked={form.required}
            onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
          />
          Mandatory
        </label>
      </div>
      {needsOptions && (
        <OptionEditor
          options={form.options}
          onChange={(options) => setForm((f) => ({ ...f, options }))}
        />
      )}
    </div>
  );
}

/**
 * Manager/Management-only config for qualification questions.
 * Sales executives only answer these on stage move — they never see this UI.
 */
export default function QualificationQuestionsBankModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [addForm, setAddForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['leadQualificationQuestions'],
    queryFn: getLeadQualificationQuestions,
    enabled: isOpen
  });

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyForm());
      setAddForm(emptyForm());
      setEditingId(null);
    }
  }, [isOpen]);

  const custom = data?.custom || [];
  const editNeedsOptions = form.type === 'select' || form.type === 'multiselect';
  const addNeedsOptions = addForm.type === 'select' || addForm.type === 'multiselect';

  const saveEditMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        label: form.label.trim(),
        type: form.type,
        required: form.required,
        transition: form.transition,
        options: editNeedsOptions
          ? form.options
              .map((o) => ({ label: o.label.trim(), value: o.value.trim() }))
              .filter((o) => o.label)
          : undefined
      };
      return updateLeadQualificationQuestion(editingId, payload);
    },
    onSuccess: () => {
      toast.success('Question updated');
      queryClient.invalidateQueries({ queryKey: ['leadQualificationQuestions'] });
      setForm(emptyForm());
      setEditingId(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save question');
    }
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        label: addForm.label.trim(),
        type: addForm.type,
        required: addForm.required,
        transition: addForm.transition,
        options: addNeedsOptions
          ? addForm.options
              .map((o) => ({ label: o.label.trim(), value: o.value.trim() }))
              .filter((o) => o.label)
          : undefined
      };
      return createLeadQualificationQuestion(payload);
    },
    onSuccess: () => {
      toast.success('Question added');
      queryClient.invalidateQueries({ queryKey: ['leadQualificationQuestions'] });
      setAddForm(emptyForm());
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to add question');
    }
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }) => updateLeadQualificationQuestion(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadQualificationQuestions'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLeadQualificationQuestion(id),
    onSuccess: (_data, id) => {
      toast.success('Question deleted');
      queryClient.invalidateQueries({ queryKey: ['leadQualificationQuestions'] });
      if (editingId === id) {
        setEditingId(null);
        setForm(emptyForm());
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete');
    }
  });

  const seedMutation = useMutation({
    mutationFn: seedLeadQualificationQuestionDefaults,
    onSuccess: (result) => {
      const n = result?.created ?? 0;
      toast.success(
        n > 0
          ? `Imported ${n} starter question${n === 1 ? '' : 's'} — edit anytime`
          : 'Starter templates already imported'
      );
      queryClient.invalidateQueries({ queryKey: ['leadQualificationQuestions'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Import failed');
    }
  });

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      label: row.label || '',
      type: row.type || 'select',
      required: row.required !== false,
      transition: row.transition || 'all',
      options:
        row.options?.length > 0
          ? row.options.map((o) => ({ value: o.value || '', label: o.label || '' }))
          : [{ value: '', label: '' }]
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const canSaveEdit = useMemo(() => {
    if (form.label.trim().length < 2) return false;
    if (editNeedsOptions && !form.options.some((o) => o.label.trim())) return false;
    return true;
  }, [form, editNeedsOptions]);

  const canAdd = useMemo(() => {
    if (addForm.label.trim().length < 2) return false;
    if (addNeedsOptions && !addForm.options.some((o) => o.label.trim())) return false;
    return true;
  }, [addForm, addNeedsOptions]);

  if (!isOpen) return null;

  return (
    <Modal name="Qualification questions" width={72} onClose={onClose}>
      <div className="space-y-5">
        <p className="text-sm text-gray-600">
          Only sales managers and management can edit this list. Sales executives only answer the
          questions you keep (checked) when they opt into qualification on stage moves. Select /
          multi-select answers become filterable tags.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Your questions</h3>
          <button
            type="button"
            className="text-xs font-medium text-[#2A565E] border border-[#2A565E] rounded-md px-2.5 py-1.5 hover:bg-[#2A565E]/5 disabled:opacity-50"
            disabled={seedMutation.isPending}
            onClick={() => seedMutation.mutate()}>
            Import starter templates
          </button>
        </div>

        <p className="text-xs text-gray-500 -mt-2">
          Check to keep a question in qualification · Uncheck to drop it · Mark Mandatory or Optional
        </p>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : custom.length === 0 ? (
          <p className="text-sm text-gray-500 border rounded-lg px-3 py-4 bg-gray-50">
            No questions yet. Add your own below, or import starter templates and edit them.
          </p>
        ) : (
          <ul className="divide-y border rounded-lg">
            {custom.map((q) => {
              const isEditing = editingId === q.id;
              const isKept = q.is_active !== false;
              return (
                <li
                  key={q.id}
                  className={`${
                    isEditing ? 'bg-[#E9F5F6]/40' : isKept ? 'hover:bg-gray-50' : 'bg-gray-50/80'
                  }`}>
                  <div className="px-3 py-2 flex items-start gap-2">
                    <label
                      className="mt-1 shrink-0 cursor-pointer"
                      title={isKept ? 'Keep in qualification' : 'Dropped — not asked on stage move'}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#2A565E]"
                        checked={isKept}
                        disabled={patchMutation.isPending}
                        onChange={(e) =>
                          patchMutation.mutate({
                            id: q.id,
                            patch: { is_active: e.target.checked }
                          })
                        }
                      />
                    </label>

                    <div className={`text-left flex-1 min-w-0 ${isKept ? '' : 'opacity-55'}`}>
                      <div className="font-medium text-gray-800 text-sm">{q.label}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                        <span>
                          {q.type} · {q.transition}
                          {q.options?.length ? ` · ${q.options.length} options` : ''}
                        </span>
                        <button
                          type="button"
                          className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${
                            q.required
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-gray-100 text-gray-600 border border-gray-200'
                          }`}
                          title="Toggle mandatory / optional"
                          disabled={patchMutation.isPending}
                          onClick={() =>
                            patchMutation.mutate({
                              id: q.id,
                              patch: { required: !q.required }
                            })
                          }>
                          {q.required ? 'Mandatory' : 'Optional'}
                        </button>
                        {!isKept && (
                          <span className="inline-flex rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-medium">
                            Dropped
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center shrink-0">
                      <button
                        type="button"
                        className={`p-1.5 rounded ${
                          isEditing
                            ? 'text-[#2A565E] bg-[#2A565E]/10'
                            : 'text-gray-400 hover:text-[#2A565E]'
                        }`}
                        onClick={() => (isEditing ? cancelEdit() : startEdit(q))}
                        title={isEditing ? 'Close edit' : 'Edit question'}>
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Permanently delete this question? Prefer unchecking to drop it instead.'
                            )
                          ) {
                            deleteMutation.mutate(q.id);
                          }
                        }}
                        title="Delete permanently">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="px-3 pb-3 pl-10 border-t border-[#2A565E]/20 pt-3 space-y-3">
                      <QuestionFormFields
                        form={form}
                        setForm={setForm}
                        needsOptions={editNeedsOptions}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 text-sm border rounded-md text-gray-700 hover:bg-gray-50"
                          onClick={cancelEdit}>
                          Cancel
                        </button>
                        <Button
                          type="button"
                          disabled={!canSaveEdit || saveEditMutation.isPending}
                          onClick={() => saveEditMutation.mutate()}>
                          Save changes
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="border rounded-lg p-3 space-y-3 bg-white">
          <div className="text-xs font-semibold text-gray-700">Add question</div>
          <QuestionFormFields
            form={addForm}
            setForm={setAddForm}
            needsOptions={addNeedsOptions}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              disabled={!canAdd || addMutation.isPending}
              onClick={() => addMutation.mutate()}>
              Add question
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
